/**
 * building-v12-labels — Diccionarios y helpers de etiquetas legibles V1.2
 *
 * @remarks
 * Fuente única de verdad para traducir valores técnicos de la API a texto
 * legible en español. Usado por el popup de mapa y los filtros del panel.
 */

// ─── Fuentes ──────────────────────────────────────────────────────────────────

export const SOURCE_TYPE_LABELS: Record<string, string> = {
  OPEN_BUILDINGS_GOOGLE: 'Open Buildings (Google)',
  MUNICIPAL_CADASTRE:    'Catastro municipal',
  USER_PROVIDED:         'Dato de usuario',
};

// ─── Calidad del dato ─────────────────────────────────────────────────────────

export const QUALITY_STATUS_LABELS: Record<string, string> = {
  high:    'Alta',
  medium:  'Media',
  low:     'Baja',
  unknown: 'Desconocida',
};

export const QUALITY_STATUS_COLORS: Record<string, string> = {
  high:    '#22c55e',
  medium:  '#f59e0b',
  low:     '#f97316',
  unknown: '#6b7280',
};

// ─── Morfología ───────────────────────────────────────────────────────────────

export const MORPHOLOGY_CLASS_LABELS: Record<string, string> = {
  small_detached_tiny:           'Pieza muy pequeña',
  small_detached:                'Edificación pequeña',
  medium_detached:               'Edificación media',
  possible_multifamily_or_mixed: 'Posible multifamiliar o mixto',
  compact_high_rise:             'Edificio compacto en altura',
  large_single_floor:            'Gran huella baja',
  medium_complex:                'Complejo mediano',
  large_high_mass_complex:       'Gran complejo de alta masa',
  requires_use_review:           'Requiere revisión',
};

/** Descripción breve (una línea) sin inferencia de uso. */
export const MORPHOLOGY_CLASS_HINTS: Record<string, string> = {
  small_detached_tiny:           'Posible anexo, cochera o módulo mínimo',
  small_detached:                'Vivienda baja probable o local pequeño',
  medium_detached:               'Pieza media independiente de planta baja',
  possible_multifamily_or_mixed: 'Huella pequeña/media con varios pisos',
  compact_high_rise:             'Torre estrecha de 6 o más pisos',
  large_single_floor:            'Gran planta con pocos pisos — galpón, comercio, etc.',
  medium_complex:                'Bloque mediano o conjunto de 3–7 pisos',
  large_high_mass_complex:       'Gran complejo con alta masa — uso indeterminado',
  requires_use_review:           'Huella ≥ 120 m² sin datos de niveles — morfología indeterminable',
};

// ─── Masa construida ──────────────────────────────────────────────────────────

export const BUILT_MASS_CLASS_LABELS: Record<string, string> = {
  minimal:   'Mínima',
  low:       'Baja',
  medium:    'Media',
  high:      'Alta',
  very_high: 'Muy alta',
};

export const BUILT_MASS_CLASS_RANGES: Record<string, string> = {
  minimal:   '< 60 m²',
  low:       '60–250 m²',
  medium:    '250–1000 m²',
  high:      '1000–5000 m²',
  very_high: '> 5000 m²',
};

// ─── Niveles ──────────────────────────────────────────────────────────────────

export const LEVELS_CONFIDENCE_LABELS: Record<string, string> = {
  high:     'Alta',
  medium:   'Media',
  low:      'Baja',
  very_low: 'Muy baja',
};

export const LEVELS_SOURCE_LABELS: Record<string, string> = {
  cadastre:       'Catastro',
  manual:         'Manual',
  height_derived: 'Derivado de altura',
  heuristic_area: 'Heurística por área',
};

// ─── Función urbana ───────────────────────────────────────────────────────────

export const FUNCTIONAL_CLASS_LABELS: Record<string, string> = {
  residential:     'Residencial',
  mixed:           'Mixto',
  non_residential: 'No residencial',
};

// ─── Estado efectivo de población (V1.2.1) ───────────────────────────────────

export const EFFECTIVE_POPULATION_STATUS_LABELS: Record<string, string> = {
  auto_eligible:   'Elegible (automático)',
  auto_excluded:   'Excluido (automático)',
  auto_unknown:    'Sin determinar',
  manual_included: 'Incluido manualmente',
  manual_excluded: 'Excluido manualmente',
};

export const MANUAL_POPULATION_STATUS_LABELS: Record<string, string> = {
  auto:    'Automático',
  include: 'Incluir',
  exclude: 'Excluir',
};

/** Microcopy explicativo para cada opción de estado de población manual */
export const MANUAL_POPULATION_STATUS_HINTS: Record<string, string> = {
  auto:    'Usa el criterio del sistema.',
  include: 'Será elegible para el cálculo poblacional.',
  exclude: 'No participará del cálculo poblacional.',
};

// ─── Helper genérico ──────────────────────────────────────────────────────────

export function labelOf(dict: Record<string, string>, key: string | null | undefined, fallback?: string): string;
export function labelOf(dict: Record<string, string>, key: string | null | undefined, fallback: null): string | null;
export function labelOf(
  dict: Record<string, string>,
  key: string | null | undefined,
  fallback: string | null = '—',
): string | null {
  if (key == null || key === '') return fallback;
  return dict[key] ?? key;
}
